<template>
	<AppLayout
		:title="trans('enrollments.selections.title')"
		:subtitle="enrollment.user.full_name"
		:return-url="route('events.enrollments.index', event.id)"
		:return-label="event.title"
	>
		<template #actions>
			<PrimaryButton :color="Color.Indigo" :title="trans('general.transfer')" @click.stop="showModal(transferModalId)">
				<i class="fa-solid fa-arrow-right-arrow-left mr-2"/>
				{{ trans('general.transfer') }}
			</PrimaryButton>
		</template>
		<Modal :id="transferModalId">
			<template #title>{{ trans('events.enrollments.transfer.title') }}</template>
			<LabeledObject>
				<template #label>{{ trans('events.enrollments.transfer.from') }}</template>
				<p>{{ enrollment.user.full_name }}</p>
			</LabeledObject>
			<LabeledObject v-if="transferUser">
				<template #label>{{ trans('events.enrollments.transfer.to') }}</template>
				<div class="flex items-center gap-3">
					<p class="grow">{{ transferUser.full_name }}</p>
					<SecondaryButton :color="Color.Red" @click.stop="transferUser = null">
						<i class="fa-solid fa-trash"/>
					</SecondaryButton>
				</div>
			</LabeledObject>
			<SearchModel v-else :label="trans('events.enrollments.transfer.to')" attribute="full_name" search-type="users" @selected="setUser"/>
			<template #footer>
				<PrimaryButton :color="Color.Green" :disabled="transferUser === null" @click.stop="transfer">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.transfer') }}
				</PrimaryButton>
			</template>
		</Modal>
		<div class="flex items-center gap-2">
			<Link v-for="tab in items" :key="tab.id" :as="tab.id === `pack-${pack?.id}` ? SecondaryButton : SoftButton" :color="Color.TrueAArEII" :href="tab.link">{{ tab.title }}</Link>
		</div>
		<Toggle v-if="pack" :event="event" :pack="pack" :enrollment="enrollment" :activities="activities" :selected-activities-ids="selectedActivitiesIds"/>
	</AppLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {Link} from '@inertiajs/vue3'
import {trans} from 'laravel-vue-i18n'
import {type User} from '@/Types/User'
import {Color} from '@/Types/Enums/Color'
import {defineProps, ref, type Ref} from 'vue'
import {type Event} from '@/Types/Events/Event'
import AppLayout from '@/Layouts/AppLayout.vue'
import Modal from '@/Components/UI/Modals/Modal.vue'
import {type Pack} from '@/Types/Events/Packs/Pack'
import {type Enrollment} from '@/Types/Events/Enrollment'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import {type TabItem} from '@/Types/Components/Tabs/TabItem'
import SearchModel from '@/Components/Search/SearchModel.vue'
import {type Activity} from '@/Types/Events/Packs/Activities/Activity'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'
import Toggle from '@/Components/Events/Enrollments/Activities/Toggle.vue'
import {closeModal, showInfo, showModal, showSuccess, showValidationErrors} from '@/Helpers/general'

const props = defineProps<{
	pack?:Pack,
	event:Event,
	enrollment:Enrollment,
	selectedPacks:Array<Pack>,
	activities:Array<Activity>,
	selectedActivitiesIds:Array<number>,
}>()

const event:Ref<Event> = ref(props.event)
const pack:Ref<Pack|null> = ref(props.pack)
const transferring:Ref<boolean> = ref(false)
const transferUser:Ref<User|null> = ref(null)
const enrollment:Ref<Enrollment> = ref(props.enrollment)
const activities:Ref<Array<Activity>> = ref(props.activities)
const selectedPacks:Ref<Array<Pack>> = ref(props.selectedPacks)
const transferModalId:Ref<string> = ref('transfer-modal')
const selectedActivitiesIds:Ref<Array<number>> = ref(props.selectedActivitiesIds)
const items:Ref<Array<TabItem>> = ref(selectedPacks.value.map((pack:Pack):TabItem => {
	return {
		id: `pack-${pack.id}`,
		title: pack.title,
		link: route('events.enrollments.packs.show', {
			event: event.value.id,
			enrollment: enrollment.value.id,
			pack: pack.id,
		}),
	}
}))

function setUser(user:User):void{
	if(user.id !== enrollment.value.user_id){
		transferUser.value = user
	} else {
		alert(trans('events.enrollments.exceptions.cannot_transfer_to_same_user'))
	}
}

function transfer():void{
	if(!transferring.value && transferUser.value !== null && enrollment.value.user_id !== transferUser.value.id && confirm(trans('events.enrollments.transfer.confirm', {
		from: enrollment.value.user.full_name,
		to: transferUser.value.full_name,
	}))){
		transferring.value = true
		showInfo(trans('events.enrollments.transfer.transferring'))
		axios.patch(route('events.enrollments.transfer', {
			event: event.value.id,
			enrollment: enrollment.value.id,
		}), {
			from: enrollment.value.user_id,
			to: transferUser.value.id,
		}).then(() => {
			showSuccess(trans('events.enrollments.transfer.transferred'))
			enrollment.value.user = transferUser.value
			enrollment.value.user_id = transferUser.value.id
			transferUser.value = null
			closeModal(transferModalId.value)
		}).catch(showValidationErrors).finally(() => {
			transferring.value = false
		})
	}
}
</script>